import axios from 'axios';

class ApiService {
  constructor() {
    // Use a singleton so there is only ever one instance
    if (ApiService.instance) {
      return ApiService.instance;
    }

    this.cmsUrl = import.meta.env.VITE_URL || 'http://localhost:1337/';

    ApiService.instance = this;
    return this;
  }

  /**
   * Get all AppTranslations
   * @returns {Promise<*|null>}
   */
  async getAppTranslations() {
    const responseData = await this._queryGraphql(`
        query {
          appTranslations(pagination: { pageSize: 100 }) {
            data {
              id
              attributes {
                key
                translation
                locale
                localizations {
                  data {
                    attributes {
                      key
                      translation
                      locale
                    }
                  }
                }
              }
            }
          }
          languages(publicationState: PREVIEW, pagination: { pageSize: 25 }) {
            data {
              id
              attributes {
                localName
                translatedName
                publishedAt
                locale
                code
              }
            }
          }
        }`);
    return {
      data: {
        appTranslations: this.formatStrapi4GraphResponse(responseData.data.appTranslations),
        languages: this.formatStrapi4GraphResponse(responseData.data.languages)
      }
    };
  }

  /**
   * Get all Courses for a given language
   * @param language
   * @returns {Promise<*|null>}
   */
  async getCourses(language) {
    const responseData = await this._queryGraphql(`
        query Courses {
          courses(locale: "${language}", sort: "sortOrder:asc", pagination: { pageSize: 25 }) {
            data {
              id
              attributes {
                name
                slug
                icon {
                  data {
                    attributes {
                      url
                    }
                  }
                }
                lessons(sort: "sortOrder:asc") {
                  data {
                    id
                    attributes {
                      name
                      slug
                      subtitle
                      sortOrder
                    }
                  }
                }
              }
            }
          }
        }`);
    return {
      data: {
        courses: this.formatStrapi4GraphResponse(responseData.data.courses)
      }
    };
  }

  /**
   * Get Course by language and slug
   * @param language
   * @param courseSlug
   * @returns {Promise<null|any>}
   */
  async getCourse(language, courseSlug) {
    const responseData = await this._queryGraphql(`
        query Courses {
          courses(filters: { slug: { eq: "${courseSlug}" } }, locale: "${language}") {
            data {
              id
              attributes {
                name
                slug
                subtitle
                description
                lessons {
                  data {
                    id
                    attributes {
                      slug
                    }
                  }
                }
              }
            }
          }
        }
      `);
    return {
      data: {
        courses: this.formatStrapi4GraphResponse(responseData.data.courses)
      }
    };
  }

  /**
   * Get single lesson by ID
   * @param id
   * @returns {Promise<null|any>}
   */
  async getLesson(id) {
    const responseData = await this._queryGraphql(`
        query Lesson {
          lesson(id: "${id}") {
            data {
              id
              attributes {
                name
                subtitle
                slug
                course {
                  data {
                    id
                    attributes {
                      slug
                    }
                  }
                }
                content {
                  ... on ComponentLessonblockActivities {
                    id
                    name
                    content
                    type
                  }
                  ... on ComponentLessonblockApplication {
                    id
                    name
                    content
                    type
                  }
                  ... on ComponentLessonblockObjectives {
                    id
                    name
                    content
                    type
                  }
                  ... on ComponentLessonblockQuestions {
                    id
                    name
                    content
                    type
                  }
                  ... on ComponentLessonblockRecap {
                    id
                    name
                    content
                    type
                  }
                  ... on ComponentLessonblockScripture {
                    id
                    name
                    heading
                    shouldDisplayName
                    content
                    audioUrl
                    type
                  }
                  ... on ComponentLessonblockSharing {
                    id
                    name
                    content
                    type
                  }
                  ... on Error {
                    code
                    message
                  }
                }
              }
            }
          }
        }
      `);
    return {
      data: {
        lesson: this.formatStrapi4GraphResponse(responseData.data.lesson)
      }
    };
  }

  /**
   * Get user answers
   * @param user
   * @returns {Promise<null|any>}
   */
  async getUserAnswers(user) {
    const responseData = await this._queryGraphql(
      `
      {
        answers(filters: {user: {id: {eq: "${user?.user?.id}"}}}) {
          data {
            id
            attributes {
              answer
              lessonQuestionId
              lessonblock {
                data {
                  id
                }
              }
            }
          }
        }
      }
    `,
      user.jwt
    );
    return {
      data: {
        answers: this.formatStrapi4GraphResponse(responseData.data.answers)
      }
    };
  }

  /**
   * Get user progress data
   * @param user
   * @returns {Promise<null|any>}
   */
  async getUserData(user) {
    const responseData = await this._queryGraphql(
      `
      query Userdata {
        userdata(filters: { userId: { eq: "${user.user.id}" } }) {
          data {
            id
            attributes {
              completedLessons
              userId
            }
          }
        }
      }
    `,
      user.jwt
    );
    return {
      data: {
        userdata: this.formatStrapi4GraphResponse(responseData.data.userdata)
      }
    };
  }

  /**
   * Save user progress data
   * @param user
   * @param userPostId
   * @param completedLessons
   * @returns {Promise<null|any>}
   */
  async saveUserData(user, userPostId, completedLessons) {
    const exists = !!userPostId;
    const mutationName = exists ? 'updateUserdatum' : 'createUserdatum';
    const whereClause = exists ? `id: "${userPostId}",` : '';
    return this._queryGraphql(
      `
      mutation {
        ${mutationName}(
          ${whereClause}
          data: { 
            completedLessons: "${completedLessons.join(',')}",
            userId:"${user.user.id}"
          }
        ) {
          data {
            id
          }
        }
      }
    `,
      user.jwt
    );

  }

  /**
   * Save/Update a user answer
   * @param user
   * @param lessonId
   * @param blockId
   * @param answerId
   * @param answerContent
   * @returns {Promise<null|any>}
   */
  async saveUserAnswer(user, lessonId, blockId, answerId, answerContent) {
    const exists = answerId !== undefined && answerId !== null;
    const mutationName = exists ? 'updateAnswer' : 'createAnswer';
    const whereClause = exists ? `id: "${answerId}",` : '';
    return this._queryGraphql(
      `
      mutation {
        ${mutationName}(
          ${whereClause}
          data: { 
            answer: "${answerContent}",
            lessonblock: "${blockId}",
            lessonQuestionId: ${blockId},
            user: "${user.user.id}"
          }
        ) {
          data {
            id
            attributes {
              answer
            }
          }
        }
      }
      `,
      user.jwt
    );

  }

  /**
   * Delete a user answer
   * @param user
   * @param answerId
   * @returns {Promise<*|null>}
   */
  async deleteUserAnswer(user, answerId) {
    return this._queryGraphql(
      `
      mutation {
        deleteAnswer(id: "${answerId}") {
          data {
            id
          }
        }
      }
      `,
      user.jwt
    );
  }

  /**
   * Delete a user account
   * @param user
   * @returns {Promise<*|null>}
   */
  async deleteUser(user) {
    return this._queryGraphql(
      `
      mutation {
        deleteUsersPermissionsUser(id: "${user.user.id}") {
          data {
            id
          }
        }
      }
      `,
      user.jwt
    );
  }

  /**
   * Send email via API
   * @param to
   * @param subject
   * @param body
   * @returns {Promise<void>}
   */
  sendEmail(to, subject, body, options) {
    const config = {
      ...options,
      to,
      subject
    };
    if (body) {
      config.html = body;
    }
    return this.sendPostRequest('send-email', config);
  }

  /**
   * Send generic POST request. Prepends `path` with proper CMS URL
   * @param path
   * @param body
   * @returns {Promise<Promise<AxiosResponse<T>>>}
   */
  async sendPostRequest(path, body) {
    const apiPath = `api/${path}`;
    return axios.post(`${this.cmsUrl}${apiPath}`, body);
  }

  /**
   * Execute a GraphQL query
   * @param query
   * @param auth - Bearer token to add to request header
   * @param apiVersion - Strapi version to use for query syntax
   * @returns {Promise<null|any>}
   * @private
   */
  async _queryGraphql(query, auth, apiVersion = 4) {
    const headers = { 'Content-Type': 'application/json' };
    if (auth) {
      headers.Authorization = `Bearer ${auth}`;
    }
    if (apiVersion) {
      headers['X-Api-Version'] = apiVersion;
    }

    let response;
    try {
      response = await axios.post(`${this.cmsUrl}graphql`, { query }, { headers });
    } catch (err) {
      if (err.response) {
        response = err.response;
        if (response.errors) { // Strapi v4 syntax
          for (const queryError of response.errors) {
            console.error(queryError.message);
          }
        } else {
          console.error(err.response);
        }
      }
      throw err;
    }

    if (response) {
      return response.data;
    }
    return null;
  }

  formatStrapi4GraphEntity(obj) {
    const result = {
      id: obj.id
    };
    for (const attr in obj.attributes) {
      result[attr] = this.formatStrapi4GraphResponse(obj.attributes[attr]);
    }
    return result;
  }

  formatStrapi4GraphResponse(obj) {
    if (obj && obj.data && Array.isArray(obj.data) && (obj.data[0].id || obj.data[0].attributes)) {
      return obj.data.map(child => this.formatStrapi4GraphEntity(child));
    } if (obj && obj.data && (obj.data.id || obj.data.attributes)) {
      return this.formatStrapi4GraphEntity(obj.data);
    }
    return obj;

  }
}
export default ApiService;
